<section class="diagnostic" data-testid="structured-product-lookup-diagnostic">
  <header>
    <p class="eyebrow">Configured discovery path</p>
    <h3>Plan and query diagnostic</h3>
    <p>
      Runs the saved chatbot → assistant → feed route through the generation-bound contract, backend structured
      planner, and typed local index only. It does not ingest, publish, accept raw Lucene syntax, or simulate
      conversation history, visual matching, retry/grouping, variant expansion, or product-card authorization.
    </p>
  </header>

  @if (loadingConfiguration) {
    <p role="status">Loading configured routes…</p>
  } @else if (configurationError) {
    <p class="diagnostic-error" role="alert">Configured chatbots and assistants could not be loaded.</p>
  } @else if (configuredChatbots.length === 0) {
    <p class="diagnostic-error" role="alert">
      No enabled chatbot → assistant route is configured for this root feed. Configure the relationship before testing.
    </p>
  } @else {
    <form [formGroup]="form" (ngSubmit)="run()" class="diagnostic-form">
      <div class="diagnostic-grid">
        <mat-form-field appearance="outline">
          <mat-label>Chatbot</mat-label>
          <mat-select formControlName="chatbotId">
            @for (chatbot of configuredChatbots; track chatbot.id) {
              <mat-option [value]="chatbot.id">{{ chatbot.name || chatbot.id }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Assistant</mat-label>
          <mat-select formControlName="assistantId">
            @for (assistant of assistantsForSelectedChatbot; track assistant.id) {
              <mat-option [value]="assistant.id">{{ assistant.name || assistant.id }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Market</mat-label>
          <input matInput formControlName="market" placeholder="Norway">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Page URL</mat-label>
          <input matInput formControlName="pageUrl" placeholder="https://example.com/no/trening">
        </mat-form-field>
      </div>

      <mat-form-field appearance="outline" class="needs-field">
        <mat-label>Customer need</mat-label>
        <textarea matInput rows="3" formControlName="needs"
                  placeholder="Finn en lett rosa treningstopp til varme økter"></textarea>
        <mat-hint align="end">{{ form.get('needs')?.value?.length || 0 }}/512</mat-hint>
        @if (form.get('needs')?.invalid && form.get('needs')?.touched) {
          <mat-error>Enter a customer need of at most 512 characters.</mat-error>
        }
      </mat-form-field>

      <div class="diagnostic-actions">
        <mat-form-field appearance="outline">
          <mat-label>Result preview</mat-label>
          <input matInput type="number" formControlName="limit" min="1" max="10">
        </mat-form-field>
        <button type="submit" mat-raised-button color="primary" [disabled]="form.invalid || running">
          {{ running ? 'Planning and querying…' : 'Run configured discovery' }}
        </button>
      </div>
    </form>
  }

  @if (requestFailed) {
    <p class="diagnostic-error" role="alert">The diagnostic request failed before a safe result was returned.</p>
  }

  @if (result; as diagnosticResult) {
    <section class="diagnostic-result" aria-live="polite">
      <div class="diagnostic-result__header">
        <div>
          <span>Status</span>
          <strong>{{ diagnosticResult.status }}</strong>
        </div>
        <dl class="timings">
          <div><dt>Configuration</dt><dd>{{ diagnosticResult.timings.configurationMilliseconds }} ms</dd></div>
          <div><dt>Contract</dt><dd>{{ diagnosticResult.timings.contractMilliseconds }} ms</dd></div>
          <div><dt>Planner</dt><dd>{{ diagnosticResult.timings.plannerMilliseconds }} ms</dd></div>
          <div><dt>Index</dt><dd>{{ diagnosticResult.timings.indexMilliseconds }} ms</dd></div>
          <div><dt>Total</dt><dd>{{ diagnosticResult.timings.totalMilliseconds }} ms</dd></div>
        </dl>
      </div>

      <dl class="scope-summary">
        <div><dt>Scope reason</dt><dd>{{ diagnosticResult.scopeReason || '—' }}</dd></div>
        <div><dt>Matched feeds</dt><dd>{{ diagnosticResult.matchedFeedIds.join(', ') || '—' }}</dd></div>
        <div><dt>Outcome reason</dt><dd>{{ diagnosticResult.reason || '—' }}</dd></div>
      </dl>

      @if (diagnosticResult.contract; as contract) {
        <mat-expansion-panel expanded>
          <mat-expansion-panel-header>
            <mat-panel-title>Generation-bound search contract</mat-panel-title>
            <mat-panel-description>{{ contract.categoryCount }} categories</mat-panel-description>
          </mat-expansion-panel-header>
          <dl class="scope-summary">
            <div><dt>Generation</dt><dd class="monospace-value">{{ contract.generationId }}</dd></div>
            <div><dt>Revision</dt><dd class="monospace-value">{{ contract.contractRevision }}</dd></div>
            <div><dt>Searchable text</dt><dd>{{ contract.searchableTextFields.join(', ') || '—' }}</dd></div>
            <div><dt>Numeric ranges</dt><dd>{{ contract.int32RangeFields.join(', ') || '—' }}</dd></div>
          </dl>
          <div class="contract-columns">
            <div>
              <h4>Categories{{ contract.categoriesTruncated ? ' (bounded preview)' : '' }}</h4>
              <div class="chips">
                @for (category of contract.categories; track category) {
                  <span>{{ category }}</span>
                }
              </div>
            </div>
            <div>
              <h4>Exact filter properties{{ contract.exactFilterFieldsTruncated ? ' (bounded preview)' : '' }}</h4>
              @for (filter of contract.exactFilters; track filter.field) {
                <p><strong>{{ filter.field }}</strong> — {{ filter.literals.join(', ') }}{{ filter.truncated ? ' …' : '' }}</p>
              }
            </div>
          </div>
        </mat-expansion-panel>
      }

      @if (diagnosticResult.planning; as planning) {
        <mat-expansion-panel expanded>
          <mat-expansion-panel-header>
            <mat-panel-title>Typed plan</mat-panel-title>
            <mat-panel-description>
              {{ planning.outcome }} · {{ planClauseCount(planning.candidate) }} candidate clause(s)
            </mat-panel-description>
          </mat-expansion-panel-header>
          @if (planning.candidate; as candidate) {
            <div class="plan-sections">
              <div><strong>Text</strong><pre>{{ candidate.text | json }}</pre></div>
              <div><strong>Exact filters</strong><pre>{{ candidate.exactFilters | json }}</pre></div>
              <div><strong>Ranges</strong><pre>{{ candidate.int32Ranges | json }}</pre></div>
              <div><strong>Categories</strong><pre>{{ candidate.categories | json }}</pre></div>
            </div>
          } @else {
            <p>No candidate plan was accepted.</p>
          }
          @if (planning.fallback; as fallback) {
            <details>
              <summary>Validated fallback ({{ planClauseCount(fallback) }} clauses)</summary>
              <pre>{{ fallback | json }}</pre>
            </details>
          }
        </mat-expansion-panel>
      }

      @if (diagnosticResult.query; as query) {
        <mat-expansion-panel expanded>
          <mat-expansion-panel-header>
            <mat-panel-title>Typed index outcome</mat-panel-title>
            <mat-panel-description>{{ query.resultCount }} result(s) · {{ query.queryNodeCount }} query nodes</mat-panel-description>
          </mat-expansion-panel-header>
          @if (query.results.length > 0) {
            <div class="result-table-wrap">
              <table class="result-table">
                <thead><tr><th>Rank</th><th>Product</th><th>Category</th><th>Feed</th><th>Properties</th></tr></thead>
                <tbody>
                  @for (product of query.results; track product.rank) {
                    <tr>
                      <td>{{ product.rank }}</td>
                      <td><strong>{{ product.name || 'Unnamed product' }}</strong><br><small>{{ product.sku || 'No SKU' }}</small></td>
                      <td>{{ product.category || '—' }}</td>
                      <td>{{ product.productFeedId || '—' }}</td>
                      <td>{{ product.color || '—' }} · {{ product.size || '—' }} · {{ product.price || '—' }} · {{ product.availability || '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <p>No products matched the accepted typed plan.</p>
          }
        </mat-expansion-panel>
      }
    </section>
  }
</section>
